import { useQuery } from '@tanstack/react-query';
import { domainsApi } from '../api';
import { queryKeys } from '../queries/keys';
import { EMPTY_DOMAIN_INDEX, buildDomainIndex, type DomainIndex } from '../utils/domainTree';

export interface DomainIndexState {
  /** Empty until the listing arrives, which every `domainTree` function answers for. */
  index: DomainIndex;
  loading: boolean;
  /** The read's failure message, or null when it has not failed. */
  error: string | null;
}

/**
 * The domain tree, indexed by id.
 *
 * A surface that only labels a domain renders on the empty index and gains its
 * labels when the listing lands, so it can read `index` and ignore the rest. A
 * surface that lets a reviewer *pick* a domain cannot: an empty index there is
 * indistinguishable from an account with no plants, so a picker would offer a
 * short list that looks complete and take a write against it. `loading` and
 * `error` are what let such a consumer say which it is and refuse the write.
 *
 * The listing changes about as often as a customer is onboarded, so it is
 * fetched once and kept.
 */
export function useDomainIndex(): DomainIndexState {
  const query = useQuery({
    queryKey: queryKeys.domains(),
    queryFn: () => domainsApi.list(),
    staleTime: Infinity,
    select: (data) => buildDomainIndex(data.accounts),
  });
  return {
    index: query.data ?? EMPTY_DOMAIN_INDEX,
    loading: query.isPending,
    error: query.error
      ? ((query.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Failed to load the plant list')
      : null,
  };
}
